import { IDapperReadDeleteEntityService, LoadingService } from '@digiwall/core';
import { DwGridDataSourceService, FieldTypes } from '@digiwall/ag-grid';
import { computed, Injector, Signal, signal, WritableSignal } from '@angular/core';
import { ColDef } from '@ag-grid-community/core';
import { LocalizationService, SubscriptionService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { Router, ActivatedRoute } from '@angular/router';
import { BaseCustomerDocList } from '../base-customer-docs/base-customer-doc-list';
import { BaseInvoiceListDto } from '@digiwall/goliath-documents/proxy';
import { DropdownAction } from '@digiwall/ui-kit';
import { Observable } from 'rxjs';
import { ColumnDefinitionsService } from '../../services/column-definitions.service';

export abstract class BaseInvoiceDocList<
  TEntityListDto extends BaseInvoiceListDto,
  DocService extends IDapperReadDeleteEntityService<TEntityListDto>,
> extends BaseCustomerDocList<TEntityListDto, DocService> {
  protected hasPeppolConfiguration: WritableSignal<boolean> = signal<boolean>(false);

  protected toolbarActions: Signal<DropdownAction[]> = computed(() => {
    return [
      {
        label: 'Doc::SendByPeppol',
        icon: 'digi-out-top',
        action: () => {
          let nbSelectedEntities = this.selectedEntities().length;
          this.confirmationService
            .warn(
              this.localizationService.instant(
                'Doc::SendByPeppolConfirmMessage',
                nbSelectedEntities.toString(),
              ),
              this.localizationService.instant('Doc::SendByPeppolConfirmTitle'),
            )
            .subscribeOnce(confirmationResult => {
              if (confirmationResult == Confirmation.Status.confirm) {
                this.loadingService.interceptNextRequest();
                this.generateUBLThenSendMultipleObservable(
                  this.selectedEntities().map(x => x.id),
                ).subscribeOnce((nbSuccess: number) => {
                  this.confirmationService.success(
                    this.localizationService.instant(
                      'Doc::SentByPeppolMessage',
                      nbSuccess.toString(),
                      (nbSelectedEntities - nbSuccess).toString(),
                    ),
                    this.localizationService.instant('Doc::SentByPeppolTitle'),
                    { hideCancelBtn: true, yesText: this.localizationService.instant('::Ok') },
                  );
                });
              }
            });
        },
        visible: computed(
          () => this.hasPeppolConfiguration() && this.selectedEntities()?.length > 0,
        ),
      },
    ];
  });

  override colDefs: ColDef[] = [
    ...this.colDefs,
    {
      field: 'dueDate',
      headerName: this.localizationService.instant('Doc::CustomerInvoice:DueDate'),
      type: FieldTypes.Date,
    },
    {
      field: 'isExported',
      headerName: this.localizationService.instant('Doc::CustomerInvoice:IsExported'),
      type: FieldTypes.Boolean,
    },
    {
      field: 'totalAccountingPayment',
      headerName: this.localizationService.instant('Doc::CustomerInvoice:TotalAccountingPayment'),
      type: FieldTypes.Currency,
    },
    {
      field: 'lastAccountingPaymentUpdateDate',
      headerName: this.localizationService.instant(
        'Doc::CustomerInvoice:lastAccountingPaymentUpdateDate',
      ),
      type: FieldTypes.Date,
    },
    {
      field: 'balanceDue',
      headerName: this.localizationService.instant('Doc::CustomerInvoice:BalanceDue'),
      type: FieldTypes.Currency,
    },
    this.columnDefinitionService.getPeppolStatusColDef(),
    this.columnDefinitionService.getPeppolSendStatusColDef(),
    {
      field: 'customerPeppolParticipantId',
      headerName: this.localizationService.instant('Contact::PeppolParticipantId'),
      type: FieldTypes.String,
    },
    {
      field: 'customerElectronicInvoice',
      headerName: this.localizationService.instant('Contact::ThirdParty:ElectronicInvoice'),
      type: FieldTypes.Boolean,
    },
  ];

  constructor(
    docService: DocService,
    router: Router,
    route: ActivatedRoute,
    localizationService: LocalizationService,
    subscriptionService: SubscriptionService,
    confirmation: ConfirmationService,
    datasource: DwGridDataSourceService,
    injector: Injector,
    private columnDefinitionService: ColumnDefinitionsService,
    private loadingService: LoadingService,
  ) {
    super(
      docService,
      router,
      route,
      localizationService,
      subscriptionService,
      confirmation,
      datasource,
      injector,
    );
  }

  protected override get enableDelete(): boolean {
    //do not delete, from task #27033
    return false;
  }

  protected abstract generateUBLThenSendMultipleObservable(ids: string[]): Observable<number>;
}
